import { useState, useEffect, useRef } from "react";
import { LifeBuoy, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import {
  PORTAIL_TICKET_TITLE_PLACEHOLDER,
  normalizeTicketTitle,
  validateTicketFields,
  type TicketFieldErrors,
} from "../lib/ticket-validation";

interface ResellerInfo {
  id: string;
  nom: string;
  logoUrl: string | null;
}

interface PublicForm {
  titre: string;
  problemDescription: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  serialNumberFree: string;
  datePanne: string;
  clientFinalNom: string;
  livraisonNomEntreprise: string;
  livraisonAdresse: string;
  livraisonContact: string;
  livraisonPhone: string;
  video: File | null;
}

const EMPTY_FORM: PublicForm = {
  titre: "", problemDescription: "", contactName: "", contactPhone: "",
  contactEmail: "", serialNumberFree: "", datePanne: "",
  clientFinalNom: "",
  livraisonNomEntreprise: "", livraisonAdresse: "",
  livraisonContact: "", livraisonPhone: "", video: null,
};

export default function TicketPublicPage() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') || '';

  const [reseller, setReseller] = useState<ResellerInfo | null>(null);
  const [loadingToken, setLoadingToken] = useState(true);
  const [tokenError, setTokenError] = useState<string | null>(null);

  const [form, setForm] = useState<PublicForm>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [createdNumber, setCreatedNumber] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<TicketFieldErrors>({});
  const submitLockRef = useRef(false);

  useEffect(() => {
    if (!token) { setTokenError("Lien invalide ou manquant."); setLoadingToken(false); return; }
    fetch(`/api/portail/token/${token}`)
      .then(r => r.json())
      .then(data => {
        if (data.reseller) setReseller(data.reseller);
        else setTokenError("Lien invalide ou expiré.");
      })
      .catch(() => setTokenError("Erreur lors de la validation du lien."))
      .finally(() => setLoadingToken(false));
  }, [token]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const validationErrors = validateTicketFields(form.titre, form.problemDescription);
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      setSubmitError(null);
      return;
    }
    if (!form.contactName.trim()) { setSubmitError("Nom du contact requis."); return; }
    if (!form.contactEmail.trim()) { setSubmitError("Email requis."); return; }
    if (submitLockRef.current) return;
    submitLockRef.current = true;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload = {
        ...form,
        titre: normalizeTicketTitle(form.titre),
        problemDescription: form.problemDescription.trim(),
      };
      const fd = new FormData();
      fd.append('token', token);
      Object.entries(payload).forEach(([k, v]) => {
        if (k === 'video') {
          if (v) fd.append('video', v as File);
        } else {
          fd.append(k, v as string);
        }
      });
      const res = await fetch('/api/portail/tickets/public', { method: 'POST', body: fd });
      if (!res.ok) {
        const err = await res.json();
        if (err.field === "titre" || err.field === "problemDescription") {
          setFieldErrors(prev => ({ ...prev, [err.field]: err.message }));
        }
        setSubmitError(err.message || "Erreur lors de l'envoi.");
        return;
      }
      const data = await res.json();
      setCreatedNumber(data.ticketNumber);
      setSuccess(true);
    } catch {
      setSubmitError("Erreur réseau. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
      submitLockRef.current = false;
    }
  }

  const formIsValid = Object.keys(
    validateTicketFields(form.titre, form.problemDescription),
  ).length === 0;

  if (loadingToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (tokenError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md text-center space-y-4">
          <AlertCircle className="w-12 h-12 mx-auto text-destructive" />
          <p className="text-foreground font-semibold">Lien invalide</p>
          <p className="text-sm text-muted-foreground">{tokenError}</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md text-center space-y-5">
          <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-9 h-9 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Ticket envoyé !</h1>
            <p className="text-muted-foreground mt-2">
              Votre demande a bien été transmise à l'équipe {reseller?.nom || 'support'}.
            </p>
            {createdNumber && (
              <p className="text-sm text-muted-foreground mt-1">
                Numéro de ticket : <span className="font-mono font-semibold text-foreground">{createdNumber}</span>
              </p>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            Vous serez recontacté par email ou téléphone selon les informations fournies.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-3">
          {reseller?.logoUrl && (
            <img src={reseller.logoUrl} alt={reseller.nom} className="w-16 h-16 object-contain mx-auto rounded-xl" />
          )}
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center justify-center gap-2">
              <LifeBuoy className="w-6 h-6" /> Déposer un ticket SAV
            </h1>
            {reseller && (
              <p className="text-muted-foreground mt-1">
                Assistance technique — {reseller.nom}
              </p>
            )}
          </div>
        </div>

        {/* Formulaire */}
        <div className="bg-card border border-border/50 rounded-2xl shadow-sm">
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            <div className="space-y-1.5">
               <label className="text-sm font-medium text-foreground">Objet *</label>
              <input
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                 placeholder={PORTAIL_TICKET_TITLE_PLACEHOLDER}
                value={form.titre}
                 onChange={e => {
                   setForm({ ...form, titre: e.target.value });
                   setFieldErrors(prev => ({ ...prev, titre: undefined }));
                 }}
                 aria-invalid={!!fieldErrors.titre}
                data-testid="input-public-titre"
              />
               {fieldErrors.titre && <p className="text-sm text-destructive">{fieldErrors.titre}</p>}
            </div>
            <div className="space-y-1.5">
               <label className="text-sm font-medium text-foreground">Description du problème *</label>
              <textarea
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                rows={4}
                placeholder="Décrivez en détail le problème rencontré..."
                value={form.problemDescription}
                 onChange={e => {
                   setForm({ ...form, problemDescription: e.target.value });
                   setFieldErrors(prev => ({ ...prev, problemDescription: undefined }));
                 }}
                 aria-invalid={!!fieldErrors.problemDescription}
                data-testid="input-public-description"
              />
               {fieldErrors.problemDescription && (
                 <p className="text-sm text-destructive">{fieldErrors.problemDescription}</p>
               )}
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Nom du client final</label>
              <input
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="Le magasin ou établissement où le robot est installé"
                value={form.clientFinalNom}
                onChange={e => setForm({ ...form, clientFinalNom: e.target.value })}
                data-testid="input-public-client-final"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Numéro de série</label>
                <input
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  placeholder="Ex : T600-XXXX"
                  value={form.serialNumberFree}
                  onChange={e => setForm({ ...form, serialNumberFree: e.target.value })}
                  data-testid="input-public-serial"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Date de panne</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  value={form.datePanne}
                  onChange={e => setForm({ ...form, datePanne: e.target.value })}
                  data-testid="input-public-date-panne"
                />
              </div>
            </div>

            <div className="border-t border-border/60 pt-4 space-y-3">
              <p className="text-sm font-medium text-foreground">Vos coordonnées *</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs text-muted-foreground">Nom complet *</label>
                  <input
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="Prénom Nom"
                    value={form.contactName}
                    onChange={e => setForm({ ...form, contactName: e.target.value })}
                    data-testid="input-public-contact-name"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs text-muted-foreground">Email *</label>
                  <input
                    type="email"
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="votre@email.com"
                    value={form.contactEmail}
                    onChange={e => setForm({ ...form, contactEmail: e.target.value })}
                    data-testid="input-public-contact-email"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground">Téléphone</label>
                <input
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  placeholder="+33 6 XX XX XX XX"
                  value={form.contactPhone}
                  onChange={e => setForm({ ...form, contactPhone: e.target.value })}
                  data-testid="input-public-contact-phone"
                />
              </div>
            </div>

            <div className="border-t border-border/60 pt-4 space-y-3">
              <p className="text-sm font-medium text-foreground">Adresse de livraison (optionnel)</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs text-muted-foreground">Nom entreprise</label>
                  <input
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    value={form.livraisonNomEntreprise}
                    onChange={e => setForm({ ...form, livraisonNomEntreprise: e.target.value })}
                    data-testid="input-public-livraison-entreprise"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs text-muted-foreground">Adresse complète</label>
                  <input
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    value={form.livraisonAdresse}
                    onChange={e => setForm({ ...form, livraisonAdresse: e.target.value })}
                    data-testid="input-public-livraison-adresse"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs text-muted-foreground">Contact</label>
                  <input
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    value={form.livraisonContact}
                    onChange={e => setForm({ ...form, livraisonContact: e.target.value })}
                    data-testid="input-public-livraison-contact"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs text-muted-foreground">Téléphone</label>
                  <input
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    value={form.livraisonPhone}
                    onChange={e => setForm({ ...form, livraisonPhone: e.target.value })}
                    data-testid="input-public-livraison-phone"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Vidéo du problème (optionnel, max 50 Mo)</label>
              <input
                type="file"
                accept="video/*"
                className="w-full text-sm text-muted-foreground file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border file:border-border file:text-xs file:bg-secondary file:text-foreground hover:file:bg-secondary/80 cursor-pointer"
                onChange={e => setForm({ ...form, video: e.target.files?.[0] || null })}
                data-testid="input-public-video"
              />
            </div>

            {submitError && (
              <div className="flex items-center gap-2 text-destructive text-sm p-3 bg-destructive/10 rounded-lg">
                <AlertCircle className="w-4 h-4 shrink-0" /> {submitError}
              </div>
            )}

            <button
              type="submit"
               disabled={submitting || !formIsValid}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-white font-medium text-sm disabled:opacity-60 transition hover:opacity-90 bg-primary"
              data-testid="button-public-submit"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {submitting ? "Envoi en cours..." : "Envoyer ma demande"}
            </button>

            <p className="text-xs text-muted-foreground text-center">
              Vos données sont transmises de façon sécurisée à l'équipe support.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
